import type {
  EnumeratedBooleanVFrame,
  EnumeratedDateVFrame,
  EnumeratedDecimalVFrame,
  EnumeratedEnumVFrame,
  EnumeratedFractionVFrame,
  EnumeratedIntegerVFrame,
  EnumeratedMoneyDecimalVFrame,
  EnumeratedMoneyVFrame,
  EnumeratedStringVFrame,
  FixedBooleanVFrame,
  FixedDateVFrame,
  FixedDecimalVFrame,
  FixedEnumVFrame,
  FixedFractionVFrame,
  FixedIntegerVFrame,
  FixedMoneyDecimalVFrame,
  FixedMoneyVFrame,
  FixedStringVFrame,
  FreeBooleanVFrame,
  FreeStringVFrame,
  RangeDateVFrame,
  RangeDecimalVFrame,
  RangeFractionVFrame,
  RangeIntegerVFrame,
  RangeMoneyDecimalVFrame,
  RangeMoneyVFrame,
  VFrameBase,
} from '@festina/life-general-types/life'
import type { Optional } from '@festina-life/core'

import { isVFrame } from './yupValidationUtils'

// Frame check type (Enumerated, Fixed, Free, Range)
export type AnyEnumeratedVFrame =
  | EnumeratedBooleanVFrame
  | EnumeratedDateVFrame
  | EnumeratedDecimalVFrame
  | EnumeratedEnumVFrame
  | EnumeratedFractionVFrame
  | EnumeratedIntegerVFrame
  | EnumeratedMoneyVFrame
  | EnumeratedMoneyDecimalVFrame
  | EnumeratedStringVFrame

export type AnyFixedVFrame =
  | FixedBooleanVFrame
  | FixedDateVFrame
  | FixedDecimalVFrame
  | FixedEnumVFrame
  | FixedFractionVFrame
  | FixedIntegerVFrame
  | FixedMoneyVFrame
  | FixedMoneyDecimalVFrame
  | FixedStringVFrame

export type AnyFreeVFrame = FreeBooleanVFrame | FreeStringVFrame

export type AnyRangeVFrame =
  | RangeDateVFrame
  | RangeDecimalVFrame
  | RangeFractionVFrame
  | RangeIntegerVFrame
  | RangeMoneyVFrame
  | RangeMoneyDecimalVFrame

// Combination of all known usable frame types (excludes DontUse)
export type AnyFrame =
  | AnyEnumeratedVFrame
  | AnyFixedVFrame
  | AnyFreeVFrame
  | AnyRangeVFrame

// Frame value type (Boolean, Date, Decimal, Enum, Fraction, Integer, Money, MoneyDecimal, String)
export type AnyBooleanVFrame =
  | EnumeratedBooleanVFrame
  | FixedBooleanVFrame
  | FreeBooleanVFrame

export type AnyDateVFrame =
  | EnumeratedDateVFrame
  | FixedDateVFrame
  | RangeDateVFrame

export type AnyDecimalVFrame =
  | EnumeratedDecimalVFrame
  | FixedDecimalVFrame
  | RangeDecimalVFrame

export type AnyEnumVFrame = EnumeratedEnumVFrame | FixedEnumVFrame

export type AnyFractionVFrame =
  | EnumeratedFractionVFrame
  | FixedFractionVFrame
  | RangeFractionVFrame

export type AnyIntegerVFrame =
  | EnumeratedIntegerVFrame
  | FixedIntegerVFrame
  | RangeIntegerVFrame

export type AnyMoneyVFrame =
  | EnumeratedMoneyVFrame
  | FixedMoneyVFrame
  | RangeMoneyVFrame

export type AnyMoneyDecimalVFrame =
  | EnumeratedMoneyDecimalVFrame
  | FixedMoneyDecimalVFrame
  | RangeMoneyDecimalVFrame

export type AnyStringVFrame =
  | EnumeratedStringVFrame
  | FixedStringVFrame
  | FreeStringVFrame

// isType based on check mode
export const isDontUseVFrame = (frame: Optional<VFrameBase>) =>
  // no AnyDontUseVFrame as it would be of no use
  !!frame?._type.startsWith('DontUse')

export const isEnumeratedVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyEnumeratedVFrame => !!frame?._type.startsWith('Enumerated')

export const isFixedVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyFixedVFrame => !!frame?._type.startsWith('Fixed')

export const isFreeVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyFreeVFrame => !!frame?._type.startsWith('Free')

export const isRangeVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyRangeVFrame => !!frame?._type.startsWith('Range')

// isType based on value types
export const isBooleanVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyBooleanVFrame => !!frame?._type.endsWith('BooleanVFrame')

export const isDateVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyDateVFrame => !!frame?._type.endsWith('DateVFrame')

export const isDecimalVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyDecimalVFrame => !!frame?._type.endsWith('DecimalVFrame')

export const isEnumVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyEnumVFrame => !!frame?._type.endsWith('EnumVFrame')

export const isFractionVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyFractionVFrame => !!frame?._type.endsWith('FractionVFrame')

export const isIntegerVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyIntegerVFrame => !!frame?._type.endsWith('IntegerVFrame')

export const isMoneyVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyMoneyVFrame => !!frame?._type.endsWith('MoneyVFrame')

export const isMoneyDecimalVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyMoneyDecimalVFrame =>
  !!frame?._type.endsWith('MoneyDecimalVFrame')

export const isStringVFrame = (
  frame: Optional<VFrameBase>,
): frame is AnyStringVFrame => !!frame?._type.endsWith('StringVFrame')

// There is no base type that has a requiredInModelInstance property, as fixed doesn't have it. So I've added all frame types.
export const isRequiredInModelFrame = (frame: Optional<AnyFrame>) =>
  !!(
    frame &&
    'requiredInModelInstance' in frame &&
    frame.requiredInModelInstance
  )

/**
 * `true` if the frame is for a primitive data type, as opposed to a composite frame for multiple fields.
 */
export const isASimpleVFrame = (frame: unknown) =>
  isVFrame(frame) &&
  (isBooleanVFrame(frame) ||
    isDateVFrame(frame) ||
    isDecimalVFrame(frame) ||
    isEnumVFrame(frame) ||
    isFractionVFrame(frame) ||
    isIntegerVFrame(frame) ||
    isMoneyVFrame(frame) ||
    isMoneyDecimalVFrame(frame) ||
    isStringVFrame(frame))
